import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, MapPin, BookOpen, Languages, HandCoins, Cpu, BadgeCheck, TrendingUp } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";

const API = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ value, label, sub, testid }) => (
  <div className="rounded-[16px] border border-[#A8722A]/25 bg-[#FFFDF8] p-5 text-center shadow-sm" data-testid={testid}>
    <p className="font-eczar text-3xl font-bold text-[#B23A2B] md:text-4xl">{value}</p>
    <p className="mt-1 font-mukta text-sm font-semibold text-[#3B221B]">{label}</p>
    {sub && <p className="font-mukta text-xs text-[#6B5044]">{sub}</p>}
  </div>
);

export default function ImpactPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useSeo({
    title: "Our Impact — ಪ್ರಭಾವ | PurohithConnect Social & Deep-Tech Metrics",
    description: "Live impact metrics for PurohithConnect — purohith livelihoods supported at 0% commission, Veda patashala students, serviceable areas across Bengaluru, and Vedic heritage digitised. Aligned with Digital India, Skill India and cultural preservation.",
    canonicalPath: "/impact",
    jsonLd: { "@context": "https://schema.org", "@type": "WebPage", name: "PurohithConnect Impact", url: "https://purohithconnect.com/impact" },
  });
  useEffect(() => {
    fetch(`${API}/api/impact/stats`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then(setD).catch(() => setErr(true));
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-8 md:py-16" data-testid="impact-page">
        <div className="text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] px-4 py-1 font-mukta text-xs text-[#A8722A]"><TrendingUp size={13} /> Live platform metrics</p>
          <h1 className="mt-3 font-eczar text-4xl font-bold sm:text-5xl">ನಮ್ಮ ಪ್ರಭಾವ · Our Impact</h1>
          <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044] md:text-lg">
            Technology for livelihoods, inclusion and the preservation of Karnataka's sacred heritage — measured live from the platform.
          </p>
        </div>

        {err ? (
          <p className="mt-10 text-center font-mukta text-[#B23A2B]" data-testid="impact-error">Could not load metrics. Please refresh.</p>
        ) : !d ? (
          <p className="mt-10 text-center font-mukta text-[#6B5044]" data-testid="impact-loading">Loading metrics…</p>
        ) : (
          <div className="mt-10 space-y-12">
            <section>
              <h2 className="inline-flex items-center gap-2 font-eczar text-2xl font-semibold"><HandCoins size={20} className="text-[#A8722A]" /> Livelihoods · ಜೀವನೋಪಾಯ</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat value={d.livelihoods.purohiths_onboarded} label="Purohiths onboarded" testid="stat-purohiths" />
                <Stat value={d.livelihoods.verified_purohiths} label="KYC-verified" testid="stat-verified" />
                <Stat value={`${d.livelihoods.commission_charged_to_purohith_pct}%`} label="Commission from purohiths" sub="free to join & earn" testid="stat-commission" />
                <Stat value={d.livelihoods.patashala_students} label="Patashala students" sub={`${d.livelihoods.patashala_graduates_verified} verified graduates`} testid="stat-students" />
              </div>
            </section>

            <section>
              <h2 className="inline-flex items-center gap-2 font-eczar text-2xl font-semibold"><MapPin size={20} className="text-[#A8722A]" /> Reach · ವ್ಯಾಪ್ತಿ</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat value={d.reach.areas_served} label="Serviceable areas" sub={d.reach.primary_city} testid="stat-areas" />
                <Stat value={d.reach.poojas_offered} label="Poojas offered" testid="stat-poojas" />
                <Stat value={d.reach.bookings_facilitated} label="Bookings facilitated" testid="stat-bookings" />
                <Stat value={d.reach.families_enquiries} label="Family enquiries" testid="stat-leads" />
              </div>
            </section>

            <section>
              <h2 className="inline-flex items-center gap-2 font-eczar text-2xl font-semibold"><BookOpen size={20} className="text-[#A8722A]" /> Heritage digitised · ಪರಂಪರೆ</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat value={d.heritage.stotras} label="Stotras" testid="stat-stotras" />
                <Stat value={d.heritage.rituals_documented} label="Rituals documented" testid="stat-rituals" />
                <Stat value={d.heritage.festivals_documented} label="Festivals documented" testid="stat-festivals" />
                <Stat value={d.heritage.total_digitised_assets} label="Total digitised assets" testid="stat-heritage-total" />
              </div>
            </section>

            <section className="rounded-[20px] border border-[#A8722A]/25 bg-[#F6ECDB] p-6 md:p-8">
              <div className="grid gap-8 md:grid-cols-2">
                <div>
                  <h2 className="inline-flex items-center gap-2 font-eczar text-2xl font-semibold"><Languages size={20} className="text-[#A8722A]" /> Inclusion · ಸೇರ್ಪಡೆ</h2>
                  <ul className="mt-3 space-y-2 font-mukta text-sm text-[#4a362c]">
                    <li className="flex gap-2"><BadgeCheck size={16} className="mt-0.5 text-[#2f6b45]" /> Kannada-first bilingual interface ({d.inclusion.languages.join(", ")})</li>
                    <li className="flex gap-2"><Cpu size={16} className="mt-0.5 text-[#2f6b45]" /> {d.inclusion.open_computation_engine}</li>
                    <li className="flex gap-2"><BadgeCheck size={16} className="mt-0.5 text-[#2f6b45]" /> AI & voice assistance for low digital-literacy users</li>
                  </ul>
                </div>
                <div>
                  <h2 className="inline-flex items-center gap-2 font-eczar text-2xl font-semibold"><Users size={20} className="text-[#A8722A]" /> Policy alignment</h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {d.alignment.map((a, i) => (
                      <span key={i} className="rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] px-3 py-1.5 font-mukta text-xs text-[#3B221B]" data-testid={`alignment-${i}`}>{a}</span>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <div className="text-center">
              <Link to="/panchanga" data-testid="impact-panchanga-link" className="inline-flex items-center gap-2 rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-white hover:bg-[#9a2f22]">
                Try the deep-tech Panchanga engine <Cpu size={16} />
              </Link>
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
